/**
 * Subscription information section component.
 *
 * Renders the active tier, cadence, status, next billing date — plus an
 * inline seat-change stepper for admins. Decreases apply at next renewal;
 * increases are charged immediately (prorated). Both go through the
 * /billing/seats/ endpoint via useBilling.changeSeats.
 */

import { Calendar, CreditCard01, Users01 } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { formatMoney } from '@/lib/billing/currency';
import type { BillingPlan, SubscriptionInfo } from '@/lib/billing/types';
import { isAllowedUrl } from '@/lib/crm/utils/urlValidation';

import { SeatStepper } from './PlanPickerSection';
import { SubscriptionStatusBadge } from './SubscriptionStatusBadge';

// External fallback for sales-led tiers whose ``sales_url`` is empty. Mirrors
// the one in PlanPickerSection — kept in sync via the same env-driven default
// on the backend (SALES_DEMO_URL settings).
const SALES_URL_FALLBACK = 'https://meet.ahoy.ai/ahoy-team';

/**
 * Map a Stripe (interval, interval_count) pair to display strings + the
 * approximate days-per-cycle figure used for proration estimates.
 *
 * Why this helper exists: the BillingTab originally hardcoded
 * "/ month" + "billed monthly" based only on ``interval``, ignoring
 * ``interval_count``. For multi-month subs ($237 per seat per 3 months,
 * for example), the page rendered "$237 / seat / month · billed monthly"
 * — a 3× under-display of what Stripe actually invoices. The cadence
 * is per-cycle, not per-month; this helper centralises the labels so
 * every display site stays in sync.
 *
 * Defined at module scope (per FRONTEND_PATTERNS.md "Hoist Constants
 * Out of Components") so the returned object has a stable reference
 * for identical inputs across renders.
 */
function getCadenceDisplay(
  interval: 'month' | 'year',
  intervalCount: number
): {
  cycleLabel: string; // "month" | "year" | "3 months" — for "/ seat / X"
  cycleLabelUpper: string; // "MONTH" | "YEAR" | "3 MONTHS" — for stat headers
  billedPhrase: string; // "billed monthly" | "billed annually" | "billed every 3 months"
  monthsPerCycle: number; // 1 | 12 | N — multiplier used for run-rate / proration math
  daysPerCycle: number; // approximate, used for proration ratio
} {
  const count = Math.max(1, intervalCount || 1);
  if (interval === 'year') {
    // Stripe technically allows interval_count > 1 on year too, but our
    // billing model treats yearly as a single 12-month cycle. Falling
    // back to that here keeps callers safe even if a malformed Price
    // ever lands a year-with-count > 1 in the response.
    return {
      cycleLabel: 'year',
      cycleLabelUpper: 'YEAR',
      billedPhrase: 'billed annually',
      monthsPerCycle: 12,
      daysPerCycle: 365,
    };
  }
  if (count === 1) {
    return {
      cycleLabel: 'month',
      cycleLabelUpper: 'MONTH',
      billedPhrase: 'billed monthly',
      monthsPerCycle: 1,
      daysPerCycle: 30,
    };
  }
  return {
    cycleLabel: `${count} months`,
    cycleLabelUpper: `${count} MONTHS`,
    billedPhrase: `billed every ${count} months`,
    monthsPerCycle: count,
    daysPerCycle: 30 * count,
  };
}

interface SubscriptionSectionProps {
  subscription: SubscriptionInfo | null;
  /** Display name of the active tier (e.g. "Pro"). */
  tierName?: string;
  /**
   * Tier slug currently active on the org (e.g. "startup"). Used to filter
   * the "Change your plan" block to non-current tiers. When omitted, the
   * change-plan block is hidden.
   */
  currentTier?: string;
  /**
   * Cadence slug currently active on the org ("monthly" or "yearly"). Used
   * to gate the "Switch to yearly billing" CTA — only shown when current
   * is monthly. The reverse direction (yearly → monthly) is intentionally
   * not exposed in self-serve.
   */
  currentCadence?: 'monthly' | 'yearly';
  /**
   * Full plan catalog. The component renders a transition button for each
   * tier where `plan.tier !== currentTier`. Self-serve targets route to the
   * Stripe Portal (via `onManageBilling`) which handles the price swap;
   * sales-led targets open `plan.sales_url` in a new tab. When omitted, the
   * change-plan block is hidden.
   */
  plans?: BillingPlan[];
  /**
   * Current confirmed seat count from the backend. When omitted, the seat
   * management block is hidden — used by call sites (e.g. legacy tests)
   * that don't surface seat info.
   */
  purchasedSeats?: number;
  /** Per-seat price in cents (active tier). */
  perSeatCents?: number | null;
  /**
   * Target lower seat count from a scheduled decrease. Null when no
   * decrease is pending. Sourced from the Stripe SubscriptionSchedule
   * attached to the subscription (read fresh every page load).
   */
  pendingSeatCount?: number | null;
  /** ISO timestamp when the scheduled decrease takes effect. */
  pendingSeatChangeAt?: string | null;
  currency?: string;
  canManageBilling: boolean;
  onManageBilling: () => void;
  /**
   * Apply a seat-count change. Required to enable the seat-change stepper.
   * Omit to hide the stepper entirely (e.g. for read-only views).
   */
  onChangeSeats?: (seatCount: number) => Promise<unknown>;
  /**
   * Switch the subscription from monthly to yearly billing. Optional —
   * when omitted, the "Switch to yearly billing" CTA is hidden even if
   * `currentCadence === 'monthly'`.
   */
  onSwitchToYearly?: () => Promise<unknown>;
  cadenceChangeLoading?: boolean;
  cadenceChangeError?: string | null;
  portalLoading?: boolean;
  /**
   * Error message from the most recent ``onManageBilling`` failure. Rendered
   * below the Manage Billing button so users get visible feedback when the
   * portal session fails to open (without re-throwing into the parent).
   */
  portalError?: string | null;
  seatChangeLoading?: boolean;
  seatChangeError?: string | null;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Date-label helpers — kept as named functions so the JSX stays readable
// without nesting ternaries. Order of precedence:
//   cancel_at_period_end > trialing > active subscription.
function nextDateLabel(sub: SubscriptionInfo): string {
  if (sub.cancel_at_period_end) {
    return 'Access ends on';
  }
  if (sub.is_trialing) {
    return 'Trial ends on';
  }
  return 'Next billing date';
}

function nextDateValue(sub: SubscriptionInfo): string {
  // Mirror nextDateLabel's precedence: a pending cancellation shows the
  // current period end (when access is revoked), regardless of trialing.
  if (sub.cancel_at_period_end) {
    return sub.current_period_end;
  }
  if (sub.is_trialing && sub.trial_end) {
    return sub.trial_end;
  }
  return sub.current_period_end;
}

interface SeatChangeCopyProps {
  seatDelta: number;
  pendingSeats: number;
  isTrialing: boolean;
  trialEnd: string | null;
  prorationEstimateCents: number | null;
  currency: string;
}

/**
 * Inline copy explaining what the pending seat change will cost (and when).
 *
 * Three branches, picked in priority order:
 *
 *   1. Decrease (seatDelta <= 0) — same message regardless of trial state;
 *      Stripe schedules the drop for next renewal, customer keeps existing
 *      seats until then.
 *   2. Increase during trial — Stripe defers all proration into the post-
 *      trial cycle, so the "you'll be charged $X today" estimate is wrong.
 *      Tell the user explicitly: no charge now, full quantity bills at
 *      trial end.
 *   3. Increase on active sub — original prorated-today copy.
 *
 * Extracted to dodge the nested-ternary lint rule and keep the parent
 * render tree readable.
 */
function SeatChangeCopy({
  seatDelta,
  pendingSeats,
  isTrialing,
  trialEnd,
  prorationEstimateCents,
  currency,
}: SeatChangeCopyProps) {
  if (seatDelta <= 0) {
    return (
      <>
        Reducing to {pendingSeats} {pendingSeats === 1 ? 'seat' : 'seats'} at
        the end of your current billing period. You keep all your current seats
        until then.
      </>
    );
  }

  const seatWord = seatDelta === 1 ? 'seat' : 'seats';

  if (isTrialing && trialEnd) {
    return (
      <>
        Adding {seatDelta} {seatWord}.{' '}
        <span className='font-semibold text-primary'>
          No charge during your trial.
        </span>{' '}
        You&apos;ll be billed for {pendingSeats}{' '}
        {pendingSeats === 1 ? 'seat' : 'seats'} when your trial ends on{' '}
        <span className='font-semibold text-primary'>
          {formatDate(trialEnd)}
        </span>
        .
      </>
    );
  }

  return (
    <>
      Adding {seatDelta} {seatWord} – you&apos;ll be charged{' '}
      <span className='font-semibold text-primary'>
        {prorationEstimateCents !== null
          ? `~${formatMoney(prorationEstimateCents, currency)} today`
          : 'a prorated amount today'}
      </span>{' '}
      for the remaining days of this billing period.
    </>
  );
}

interface ChangePlanRowProps {
  plan: BillingPlan;
  onSelfServeSwitch: () => void;
  portalLoading: boolean;
}

/**
 * One row in the "Change your plan" block — represents a transition to a
 * non-current tier. Sales-led tiers open the booking URL; self-serve tiers
 * route through the Stripe Portal which handles the actual price swap.
 *
 * Kept as an extracted component so the parent map() in the change-plan
 * block stays readable and each row gets a stable identity for React.
 */
function ChangePlanRow({
  plan,
  onSelfServeSwitch,
  portalLoading,
}: ChangePlanRowProps) {
  const isSalesLed = !plan.self_serve;

  const handleClick = useCallback(() => {
    if (isSalesLed) {
      const resolved = plan.sales_url || SALES_URL_FALLBACK;
      const target = isAllowedUrl(resolved) ? resolved : SALES_URL_FALLBACK;
      if (target !== resolved) {
        console.warn(
          'ChangePlanRow: invalid sales_url rejected, using fallback',
          { resolved }
        );
      }
      window.open(target, '_blank', 'noopener,noreferrer');
      return;
    }
    onSelfServeSwitch();
  }, [isSalesLed, plan.sales_url, onSelfServeSwitch]);

  return (
    <Button
      color='secondary'
      size='sm'
      onClick={handleClick}
      isLoading={!isSalesLed && portalLoading}
      isDisabled={!isSalesLed && portalLoading}
    >
      {isSalesLed ? `Talk to sales – ${plan.name}` : `Switch to ${plan.name}`}
    </Button>
  );
}

export function SubscriptionSection({
  subscription,
  tierName,
  currentTier,
  currentCadence,
  plans,
  purchasedSeats,
  perSeatCents,
  pendingSeatCount,
  pendingSeatChangeAt,
  currency = 'USD',
  canManageBilling,
  onManageBilling,
  onChangeSeats,
  onSwitchToYearly,
  cadenceChangeLoading,
  cadenceChangeError,
  portalLoading,
  portalError,
  seatChangeLoading,
  seatChangeError,
}: SubscriptionSectionProps) {
  // External sales-calendar link. Stable handler so re-renders don't
  // create a new function on every paint (per FRONTEND_PATTERNS.md
  // "Stable References"). Uses ``window.open`` rather than the Button's
  // ``href`` prop because the project's Button wraps react-aria-components
  // Link, which routes through React Router and treats external https://
  // URLs as relative paths — produced
  // ``/1/settings/billing/https:/meet.ahoy.ai/ahoy-team`` (the protocol's
  // double-slash even got collapsed by URL normalisation). Mirrors the
  // existing pattern in ChangePlanRow.
  const handleBookSalesCall = useCallback(() => {
    window.open(SALES_URL_FALLBACK, '_blank', 'noopener,noreferrer');
  }, []);

  // Switching plans from a sales-led tier (e.g. Pro/Growth) is not self-serve.
  // Rather than render per-tier switch buttons — which would offer a self-serve
  // downgrade to Startup — we surface a single "Talk to sales" action that
  // routes to the current tier's sales_url (falling back to the shared link).
  const handleSwitchPlanSales = useCallback(() => {
    const currentPlan = plans?.find(p => p.tier === currentTier);
    const resolved = currentPlan?.sales_url || SALES_URL_FALLBACK;
    const target = isAllowedUrl(resolved) ? resolved : SALES_URL_FALLBACK;
    if (target !== resolved) {
      console.warn(
        'handleSwitchPlanSales: invalid sales_url rejected, using fallback',
        { resolved }
      );
    }
    window.open(target, '_blank', 'noopener,noreferrer');
  }, [plans, currentTier]);

  // Local pending seat count for the stepper. Defaults to the confirmed
  // seat count (or 1 when the caller didn't provide one — in which case
  // the seat block is hidden anyway). Mirrors `purchasedSeats` until the
  // user starts editing.
  const confirmedSeats = purchasedSeats ?? 1;
  const [pendingSeats, setPendingSeats] = useState<number>(confirmedSeats);

  useEffect(() => {
    setPendingSeats(confirmedSeats);
  }, [confirmedSeats]);

  const handleApply = useCallback(async () => {
    if (!onChangeSeats) {
      return;
    }
    if (pendingSeats === confirmedSeats) {
      return;
    }
    try {
      await onChangeSeats(pendingSeats);
    } catch {
      // Error surfaces via seatChangeError prop; reset the local stepper
      // so the user can retry from a known-good baseline.
      setPendingSeats(confirmedSeats);
    }
  }, [pendingSeats, confirmedSeats, onChangeSeats]);

  /**
   * Undo a scheduled decrease. Calls `onChangeSeats(confirmedSeats)` — the
   * backend interprets "target equals current" as "release the schedule"
   * (see helpers.change_subscription_seat_count). After the response,
   * `pendingSeatCount` will be null on the next billing-page refresh.
   */
  const handleUndoPendingDecrease = useCallback(async () => {
    if (!onChangeSeats) {
      return;
    }
    try {
      await onChangeSeats(confirmedSeats);
      setPendingSeats(confirmedSeats);
    } catch {
      // Error surfaces via seatChangeError prop.
    }
  }, [confirmedSeats, onChangeSeats]);

  const handleSwitchToYearly = useCallback(async () => {
    if (!onSwitchToYearly) {
      return;
    }
    try {
      await onSwitchToYearly();
    } catch {
      // Error surfaces via cadenceChangeError prop.
    }
  }, [onSwitchToYearly]);

  // "Switch to yearly billing" eligibility — only shown when the customer
  // is on monthly AND their current tier has a yearly price configured.
  // The discount badge ("Save 20%") is sourced from the tier's marketing
  // metadata so we don't hardcode the number.
  const yearlySwitchInfo = useMemo<{
    discountPercent: number;
    eligible: boolean;
  }>(() => {
    if (currentCadence !== 'monthly' || !onSwitchToYearly) {
      return { discountPercent: 0, eligible: false };
    }
    const currentPlan = plans?.find(p => p.tier === currentTier);
    const hasYearlyPrice = currentPlan?.prices.some(
      p => p.cadence === 'yearly'
    );
    if (!hasYearlyPrice) {
      return { discountPercent: 0, eligible: false };
    }
    return {
      discountPercent: currentPlan?.yearly_discount_percent ?? 0,
      eligible: true,
    };
  }, [currentCadence, currentTier, onSwitchToYearly, plans]);

  const seatDelta = pendingSeats - confirmedSeats;

  // Estimated prorated charge for a seat increase. We approximate the
  // current billing period as 30 days (monthly) or 365 days (yearly) — the
  // real period length varies (28-31 days for months, 365/366 for years)
  // but the `~` prefix in the rendered copy signals that this is an
  // estimate. Stripe will compute the exact figure server-side when
  // `change_subscription_seat_count` actually fires.
  //
  // Returns `null` when we can't compute (no subscription, no period_end,
  // no per-seat price, or seat change is a decrease/no-op) — the UI falls
  // back to a vague-but-still-correct copy in that case.
  // Resolve cadence labels + per-cycle math from (interval, interval_count).
  // Memoised so identical subscription state produces the same object
  // reference across renders (matters for any downstream consumer that
  // memoises off this — and keeps the dependency array of the proration
  // useMemo below stable). For multi-month intervals the daysPerCycle
  // grows proportionally so the proration ratio stays accurate.
  const cadence = useMemo(
    () =>
      getCadenceDisplay(
        subscription?.billing_interval ?? 'month',
        subscription?.billing_interval_count ?? 1
      ),
    [subscription?.billing_interval, subscription?.billing_interval_count]
  );

  const prorationEstimateCents = useMemo<number | null>(() => {
    if (seatDelta <= 0) {
      return null;
    }
    if (perSeatCents === undefined || perSeatCents === null) {
      return null;
    }
    if (!subscription?.current_period_end) {
      return null;
    }
    const endMs = new Date(subscription.current_period_end).getTime();
    if (Number.isNaN(endMs)) {
      return null;
    }
    const remainingMs = Math.max(0, endMs - Date.now());
    const dayMs = 24 * 60 * 60 * 1000;
    // daysPerCycle now respects billing_interval_count — for a quarterly
    // sub this is 90 days, not the previously-hardcoded 30. Without that
    // fix, the prorated upgrade estimate would have been 3× too high.
    const ratio = Math.min(1, remainingMs / (cadence.daysPerCycle * dayMs));
    return Math.max(0, Math.round(seatDelta * perSeatCents * ratio));
  }, [
    seatDelta,
    perSeatCents,
    subscription?.current_period_end,
    cadence.daysPerCycle,
  ]);

  if (!subscription) {
    return null;
  }

  // The seat-management block is only meaningful when the caller wires up
  // both the count and the mutation. Pre-Stripe-seat callers (tests,
  // legacy paths) don't pass these, so the block hides itself.
  const seatBlockEnabled =
    purchasedSeats !== undefined && onChangeSeats !== undefined;
  // When the active tier is sales-led (self_serve === false, e.g. Pro/Growth),
  // changing away from it goes through sales — so the change-plan block shows a
  // single "Talk to sales" CTA instead of per-tier self-serve switch buttons.
  const currentPlan = plans?.find(p => p.tier === currentTier);
  const currentPlanSalesLed =
    currentPlan !== undefined && !currentPlan.self_serve;
  // The change-plan block renders when there's at least one other tier to move
  // to. For sales-led tiers it's a single "Talk to sales" CTA — which makes the
  // separate "Book a call" block below redundant, so we suppress that block in
  // exactly that case (it still shows for self-serve tiers and when no
  // change-plan block renders, so admins always have one sales affordance).
  const changePlanBlockShown = Boolean(
    currentTier && plans && plans.filter(p => p.tier !== currentTier).length > 0
  );
  const showBookACall = !(changePlanBlockShown && currentPlanSalesLed);
  const totalPerPeriod =
    perSeatCents !== undefined && perSeatCents !== null
      ? perSeatCents * confirmedSeats
      : null;

  return (
    <div className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm overflow-hidden'>
      <div className='px-6 py-5 border-b border-border-secondary'>
        <div className='flex items-center gap-3'>
          <FeaturedIcon
            icon={CreditCard01}
            size='sm'
            theme='modern'
            color='gray'
          />
          <div>
            <h2 className='text-lg font-semibold text-secondary'>
              Subscription
            </h2>
            <p className='text-sm text-tertiary mt-0.5'>
              Manage your plan and seats
            </p>
          </div>
        </div>
      </div>

      <div className='p-6 space-y-6'>
        {/* Hero — tier name + status as one row, pricing as one human-readable
            phrase below it. Replaces the old four-label grid which made the
            reader assemble "what am I paying" themselves. */}
        <div className='space-y-2'>
          <div className='flex flex-wrap items-center gap-x-3 gap-y-2'>
            <h3 className='text-2xl font-semibold tracking-tight text-primary'>
              {tierName || subscription.display_name}
            </h3>
            <SubscriptionStatusBadge
              status={subscription.status}
              cancelAtPeriodEnd={subscription.cancel_at_period_end}
            />
          </div>
          {/* Tagline always shows the per-MONTH equivalent — operators
              and end-customers reason in monthly terms even when their
              actual Stripe invoice arrives quarterly or yearly. We
              annotate the cadence ("billed every 3 months") so the
              cycle isn't hidden, but the headline number is always
              normalised to /month for easy comparison across plans.
              Stripe stores ``unit_amount`` per-invoice, so we divide by
              ``monthsPerCycle`` (1 for monthly, 12 for yearly, 3-6 for
              custom intervals) to recover the operator-typed monthly
              figure. The per-cycle figure remains visible via the
              "Total / <cycle>" stat block below. */}
          <p className='text-sm text-tertiary'>
            {perSeatCents !== undefined && perSeatCents !== null ? (
              <>
                <span className='font-medium text-secondary'>
                  {formatMoney(
                    Math.round(perSeatCents / cadence.monthsPerCycle),
                    currency
                  )}
                </span>
                {' / seat / month · '}
                {cadence.billedPhrase}
              </>
            ) : (
              <>Billed {cadence.billedPhrase.replace('billed ', '')}</>
            )}
          </p>
        </div>

        {/* Stats grid — total + next charge as a dl. Compact, readable,
            no unnecessary labels. */}
        <dl className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
          {totalPerPeriod !== null && (
            <div>
              <dt className='text-xs uppercase tracking-wide text-tertiary'>
                Total / {cadence.cycleLabelUpper}
              </dt>
              <dd className='mt-1 text-base font-medium text-primary'>
                {formatMoney(totalPerPeriod, currency)}
                {confirmedSeats > 1 && (
                  <span className='ml-2 text-sm font-normal text-tertiary'>
                    ({confirmedSeats} seats)
                  </span>
                )}
              </dd>
            </div>
          )}
          <div>
            <dt className='flex items-center gap-1.5 text-xs uppercase tracking-wide text-tertiary'>
              <Calendar aria-hidden='true' className='h-3.5 w-3.5' />
              {nextDateLabel(subscription)}
            </dt>
            <dd className='mt-1 text-base font-medium text-primary'>
              {formatDate(nextDateValue(subscription))}
            </dd>
          </div>
        </dl>

        {/* Switch-to-yearly CTA — placed right after the stats grid so it
            sits at the seam between "what am I paying" and "what can I
            change". This is the moment in the read order where the
            "could I pay less?" question is most active. Only renders when
            the customer is on monthly AND a yearly price exists for their
            tier; yearly → monthly is intentionally never exposed. */}
        {yearlySwitchInfo.eligible && (
          <div className='flex flex-col gap-2'>
            <div className='flex flex-col gap-3 rounded-lg border border-[var(--color-brand-600)] bg-[var(--color-bg-secondary)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between'>
              <div>
                <p className='text-sm font-medium text-primary'>
                  Switch to yearly billing
                  {yearlySwitchInfo.discountPercent > 0 && (
                    <span className='ml-2 rounded-full bg-[var(--color-brand-600)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-primary_on-brand)]'>
                      Save {yearlySwitchInfo.discountPercent}%
                    </span>
                  )}
                </p>
                <p className='mt-0.5 text-xs text-tertiary'>
                  Get a lower per-seat rate and one annual invoice instead of
                  monthly. The prorated difference is charged today; unused
                  monthly time is credited.
                </p>
              </div>
              <Button
                color='primary'
                size='sm'
                onClick={handleSwitchToYearly}
                isLoading={cadenceChangeLoading}
                isDisabled={cadenceChangeLoading}
              >
                Switch to yearly
              </Button>
            </div>
            {cadenceChangeError && (
              <p className='text-xs text-[var(--color-text-error-primary)]'>
                {cadenceChangeError}
              </p>
            )}
          </div>
        )}

        {/* Seat management — single labelled row. The SeatStepper renders the
            internal "Seats" label which doubles as the field label, so there's
            no separate "Seats" heading here — we keep the label-once principle. */}
        {seatBlockEnabled && (
          <div className='pt-6 border-t border-border-secondary'>
            <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
              <div className='flex items-start gap-3'>
                <Users01
                  aria-hidden='true'
                  className='mt-0.5 h-4 w-4 text-tertiary'
                />
                <div>
                  <p className='text-sm font-medium text-primary'>
                    {confirmedSeats} active{' '}
                    {confirmedSeats === 1 ? 'seat' : 'seats'}
                  </p>
                  <p className='mt-0.5 text-xs text-tertiary'>
                    {subscription.is_trialing
                      ? 'Free to add during your trial. Decreases take effect at next renewal.'
                      : 'Increases are prorated. Decreases take effect at next renewal.'}
                  </p>
                </div>
              </div>
              {canManageBilling && (
                <div className='flex items-center gap-3'>
                  <SeatStepper
                    value={pendingSeats}
                    onChange={setPendingSeats}
                    ariaLabel='Seat count'
                    compact
                  />
                  <Button
                    color='secondary'
                    size='sm'
                    onClick={handleApply}
                    isDisabled={
                      pendingSeats === confirmedSeats || seatChangeLoading
                    }
                    isLoading={seatChangeLoading}
                  >
                    {pendingSeats > confirmedSeats ? 'Add seats' : 'Update'}
                  </Button>
                </div>
              )}
            </div>
            {/* Pending scheduled decrease — surfaces a banner when the
                customer has scheduled a future decrease via the stepper.
                The data comes from the Stripe SubscriptionSchedule
                (fetched fresh each billing-page load). Includes an "Undo"
                action that releases the schedule. */}
            {pendingSeatCount !== null &&
              pendingSeatCount !== undefined &&
              pendingSeatChangeAt &&
              canManageBilling && (
                <div className='mt-3 flex flex-col gap-2 rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] px-3 py-2 sm:flex-row sm:items-center sm:justify-between'>
                  <p className='text-xs text-tertiary'>
                    Scheduled to reduce to{' '}
                    <span className='font-semibold text-primary'>
                      {pendingSeatCount}{' '}
                      {pendingSeatCount === 1 ? 'seat' : 'seats'}
                    </span>{' '}
                    on{' '}
                    <span className='font-semibold text-primary'>
                      {formatDate(pendingSeatChangeAt)}
                    </span>
                    . You keep your current seats until then.
                  </p>
                  <Button
                    color='tertiary'
                    size='sm'
                    onClick={handleUndoPendingDecrease}
                    isDisabled={seatChangeLoading}
                    isLoading={seatChangeLoading}
                  >
                    Undo
                  </Button>
                </div>
              )}
            {pendingSeats !== confirmedSeats && canManageBilling && (
              <p className='mt-3 text-xs text-tertiary'>
                <SeatChangeCopy
                  seatDelta={seatDelta}
                  pendingSeats={pendingSeats}
                  isTrialing={Boolean(subscription.is_trialing)}
                  trialEnd={subscription.trial_end}
                  prorationEstimateCents={prorationEstimateCents}
                  currency={currency}
                />
              </p>
            )}
            {seatChangeError && (
              <p className='mt-3 text-xs text-[var(--color-text-error-primary)]'>
                {seatChangeError}
              </p>
            )}
          </div>
        )}

        {/* Plan actions — change-plan transitions and the Manage Billing
            escape hatch live together as a single "secondary actions" block.
            Previously these were two separate divider-bound sections, which
            doubled as visual noise without clarifying the difference. */}
        {canManageBilling && (
          <div className='pt-6 border-t border-border-secondary'>
            {changePlanBlockShown && (
              <div className='mb-5'>
                <p className='text-sm font-medium text-primary'>
                  Looking to change your plan?
                </p>
                <p className='mt-0.5 text-xs text-tertiary'>
                  Talk to our team about an upgrade or a custom plan.
                </p>
                <div className='mt-3 flex flex-wrap gap-2'>
                  {currentPlanSalesLed ? (
                    <Button
                      color='secondary'
                      size='sm'
                      onClick={handleSwitchPlanSales}
                    >
                      Switch plan – Talk to sales
                    </Button>
                  ) : (
                    (plans ?? [])
                      .filter(p => p.tier !== currentTier)
                      .map(plan => (
                        <ChangePlanRow
                          key={plan.tier}
                          plan={plan}
                          onSelfServeSwitch={onManageBilling}
                          portalLoading={portalLoading ?? false}
                        />
                      ))
                  )}
                </div>
              </div>
            )}
            {/* "Questions about your plan?" CTA — a general "book a call"
                affordance for orgs that can manage billing. Shown for
                self-serve tiers (whose change-plan block has no sales button)
                and whenever no change-plan block renders, so admins always
                have at least one sales affordance. Suppressed on sales-led
                tiers (Pro/Growth) where the change-plan block already shows a
                "Talk to sales" button — avoids two adjacent sales CTAs. */}
            {showBookACall && (
              <div className='flex flex-wrap items-center justify-between gap-3 mb-3 pb-3 border-b border-border-secondary'>
                <div>
                  <p className='text-sm font-medium text-primary'>
                    Questions about your plan or pricing?
                  </p>
                  <p className='mt-0.5 text-xs text-tertiary'>
                    Book a quick call with our team – custom seat counts,
                    multi-month billing, and bespoke pricing all on the table.
                  </p>
                </div>
                <Button
                  color='secondary'
                  size='sm'
                  onClick={handleBookSalesCall}
                >
                  Book a call →
                </Button>
              </div>
            )}
            <div className='flex flex-wrap items-center justify-between gap-3'>
              <p className='text-xs text-tertiary'>
                Update payment, change cadence, view invoices, or cancel –
                managed by Stripe.
              </p>
              <Button
                color='tertiary'
                size='sm'
                onClick={onManageBilling}
                isLoading={portalLoading}
                isDisabled={portalLoading}
              >
                Manage billing →
              </Button>
            </div>
            {portalError && (
              <p className='mt-3 text-xs text-[var(--color-text-error-primary)]'>
                {portalError}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
